import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ViewSlabsService } from './view-slabs.service';
import { Table, Pagination, TableActions, TableAction, TableData, TableDataTypes, SlabFilters } from '../shared/types';
import { SharedService } from '../shared/shared.service';
import { LoaderService } from '../loader.service';
import { ToasterService } from '../toaster.service';
import Utils from 'src/app/utils/utils';
import { AuthzAccessHintService } from '../shared/authz-access-hint/authz-access-hint.service';


@Component({
  selector: 'view-slabs',
  templateUrl: './view-slabs.component.html',
  providers: [ViewSlabsService]
})
export class ViewSlabsComponent implements OnInit {

  public authzAccessHint: { message: string; requestUrl: string } | null = null;
  public limit = 15;
  public gotData = false;
  public offset = 0;
  dataLoaded = false;
  tableLoaded = false;
  public table: Table;
  public cities: [] = [];
  public services = [];
  public shifts = [];
  public filterComponents: any[] = [];
  public cityMap = {};
  public serviceMap = {};
  public filterParams: SlabFilters = {};
  public currentFilters = {}; // Track all current filter selections
  public showFilters = false;
  public shiftSearch: string;
  public earningModelOptions: string[] = [];

  constructor(
    private viewSlabService: ViewSlabsService,
    private sharedService: SharedService,
    private loaderService: LoaderService,
    public router: Router,
    private route: ActivatedRoute,
    public toasterService: ToasterService,
    private authzAccessHintService: AuthzAccessHintService,
  ) {
    this.defineTable();
   }

   defineTable() {
    this.table = new Table({
      headers: {
        active: 'Status',
        cities: 'City',
        shift: 'Shift Name',
        serviceTypes: 'Service',
        startDate: 'Start Date',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        isFallbackSlab: 'Fallback Slab',
        poolConfig: 'Pool Configuration',
        action: 'Action',
      },
      sortableColumnKeys: [],
      pagination: new Pagination(0, 15, 10, [15, 20]),
    });
    this.tableLoaded = true;
   }


fetchTableData() {
  this.loaderService.openLoading();

  this.viewSlabService.fetchSlabData(this.buildPayload()).subscribe(
    (res: any) => {
      const slabs = res && res.slabs ? res.slabs : [];
      const pageNo = this.table.pagination.pageNo;
      const perPage = this.table.pagination.perPage;

      if (!slabs.length && pageNo > 0) {
        this.table.pagination.count = pageNo * perPage;
        this.table.pagination.pageNo--;
        return this.fetchTableData();
      }

      this.table.data = slabs.map(this.mapSlabToTableRow.bind(this));

      const totalSlabCount =
        res != null && res.count != null ? Number(res.count) : Number.NaN;
      this.table.pagination.count = totalSlabCount
      this.dataLoaded = true;
      this.loaderService.closeLoading();
    },
    () => this.loaderService.closeLoading()
  );
  }

  private buildPayload() {
    return {
      limit: this.table.pagination.perPage,
      offset: this.table.pagination.pageNo * this.table.pagination.perPage,
      filters: { ...this.filterParams },
    };
  }

  private mapSlabToTableRow(slab: any): any {
    const cities = slab.cities || [];
    const shift = Array.isArray(slab.shift) ? slab.shift: [];
    const serviceNames = Array.isArray(slab.serviceNames) ? slab.serviceNames : [];

    const poolConfig = Utils.formatPoolConfigFromSlab(slab);

    return Object.assign({}, slab, {
      cities: cities.join(', '),
      shift: shift.join(', '),
      serviceTypes: serviceNames.join(', '),
      poolConfig: poolConfig,
      action: this.createAction(),
    });
  }

    fetchAllCities(): Promise<void> {
      return this.sharedService.fetchCities().toPromise().then((res: any) => {
        const cities = Array.isArray(res && res.data && res.data.cities) ? res.data.cities : [];
        this.cities = cities.map(city => city.displayName);
        this.patchFilterComponent('slab-city', { values: this.cities });
        for (const city of cities) {
          if (city) {
            this.cityMap[city.displayName] = city._id;
          }
        }
      }).catch(() => undefined);
    }

    fetchServices(city: string[]): Promise<void> {
      this.services = [];
      if (!city || city.length === 0) {
        this.patchFilterComponent('slab-services', { values: this.services });
        return Promise.resolve();
      }
      const requests = city.filter(Boolean).map((cty) => {
        const cityId = this.cityMap[cty];
        if (!cityId) {
          return Promise.resolve();
        }
        return this.sharedService.fetchServices(cityId).toPromise().then((res: any) => {
          for (const service in res.data) {
            if (this.services.indexOf(res.data[service].service.name) === -1) {
              this.services.push(res.data[service].service.name);
            }
          }
          for (const service in res.data) {
            if (res.data[service].service) {
              this.serviceMap[res.data[service].service.name] = res.data[service]._id;
            }
          }
        }).catch(() => undefined);
      });
      return Promise.all(requests).then(() => {
        this.patchFilterComponent('slab-services', { values: this.services });
      });
    }

    fetchShifts(city) {
      this.shifts = [];
      if (city.length === 0) {
        this.patchFilterComponent('slab-shifts', { values: [] });
      }
      for (const cty of city) {
        if (cty) {
          this.sharedService.fetchShifts(cty).subscribe((res: any) => {
            this.shifts.push(...res.data);
            this.patchFilterComponent('slab-shifts', { values: this.shifts });
          });
        }
      }
    }

    createAction() {
      return new TableData({
        actions : new TableActions([
          new TableAction({
            onClick: (data) => {
              this.onViewSlab(data);
            },
            text: 'View'
          }),
          new TableAction({
            onClick: (data) => {
              this.onDuplicateSlab(this.getSlabId(data));
            },
            text: 'Duplicate'
          })
        ]),
        type: TableDataTypes.ACTION
      });
    }

    private getSlabId(row: any): string {
      return row && row.id ? row.id : '';
    }

    private onViewSlab(row: any) {
      const slabId = this.getSlabId(row);
      if (!slabId) {
        return;
      }
      const url = this.router.serializeUrl(this.router.createUrlTree(['/slab/view', slabId]));
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    private onDuplicateSlab(slabId) {
      if (environment.isDuplicateSlabFlowEnabledInPayrollDash) {
        const url = this.router.serializeUrl(this.router.createUrlTree(['/slab/duplicate', slabId]));
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const url = '/payroll/duplicateSlab/' + slabId;
        this.sharedService.redirectToDashbaord(url);
      }
    }

    filterToggle() {
      this.showFilters = !this.showFilters;
    }

  ngOnInit() {
    this.loadAccessHint();
    // Initialize currentFilters first, before components emit their defaults
    this.currentFilters = {
      'slab-status': 'Both',
      'slab-fallback': 'All'
    };

    // call city api, assign names to cities []
    this.filterComponents = [
      {
        type: 'searchable-dropdown',
        title: 'City',
        values: this.cities,
        default: '',
        key: 'slab-city'
      },
      {
        type: 'searchable-dropdown',
        title: 'Services',
        values: this.services,
        default: '',
        key: 'slab-services'
      },
      {
        type: 'searchable-dropdown',
        title: 'Shifts',
        values: this.shifts,
        default: '',
        key: 'slab-shifts'
      },
      {
        type : 'calendar',
        title : '',
        values : [],
        default : '',
        key : 'slab-dates'
      },
      // {
      //   type : 'single-searchable-dropdown',
      //   title : 'Type',
      //   values : ['cm', 'order', 'distance'],
      //   default : '',
      //   key: 'slab-type'
      // },
      {
        type: 'searchable-dropdown',
        title: 'Earning Model',
        values: this.earningModelOptions,
        default: '',
        key: 'slab-earning-model'
      },
      {
        type : 'radio-button',
        title : 'Status',
        values : ['Both', 'Active', 'Inactive'],
        default : 'Both',
        name: 'status-radio',
        key: 'slab-status'
      },
      {
        type : 'radio-button',
        title : 'Fallback Slab',
        values : ['All', 'Fallback Only'],
        default : 'All',
        name: 'fallback-slab-radio',
        key: 'slab-fallback'
      }
    ];

    this.applyRouteQueryParamsToFilters();
    this.fetchAllCities()
      .then(() => this.applyCityServiceQueryParams())
      .then(() => {
        this.rebuildFilterParamsFromCurrentFilters();
        this.fetchTableData();
      });

    this.sharedService.fetchDashboardConfig().subscribe((res: any) => {
      const dashboardConfig = this.sharedService.parseDashboardConfigResponse(res);
      this.earningModelOptions = this.sharedService.collectAllEarningModelsFromDashboardConfig(dashboardConfig);
      this.filterComponents = this.filterComponents.map((node) => {
        if (node.key === 'slab-earning-model') {
          const merged = (this.earningModelOptions || []).slice();
          const init = node.initialSelected && node.initialSelected[0];
          if (init && merged.indexOf(init) === -1) {
            merged.unshift(init);
          }
          node.values = merged;
        }
        return node;
      });
    });
  }

  private async loadAccessHint(): Promise<void> {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('slab');
  }

  private patchFilterComponent(key: string, patch: any): void {
    this.filterComponents = this.filterComponents.map((node) => {
      if (!node || node.key !== key) {
        return node;
      }
      return { ...node, ...patch };
    });
  }

  private refreshFilterComponents(): void {
    // New object refs so SearchableDropdown re-reads initialSelected.
    this.filterComponents = this.filterComponents.map((node) => ({ ...node }));
  }

  private applyRouteQueryParamsToFilters(): void {
    const q = this.route.snapshot.queryParamMap;
    const shift = (q.get('shift') || '').trim();
    const earningModel = (q.get('earningModel') || '').trim();
    if (!shift && !earningModel) {
      return;
    }
    if (shift) {
      this.currentFilters['slab-shifts'] = [shift];
      const existing = this.filterComponents.find((node) => node.key === 'slab-shifts');
      const vals = Array.isArray(existing && existing.values) ? existing.values.slice() : [];
      if (vals.indexOf(shift) === -1) {
        vals.unshift(shift);
      }
      this.patchFilterComponent('slab-shifts', {
        values: vals,
        initialSelected: [shift]
      });
      if (this.shifts.indexOf(shift) === -1) {
        this.shifts = [shift].concat(this.shifts);
      }
    }
    if (earningModel) {
      this.currentFilters['slab-earning-model'] = [earningModel];
      const existing = this.filterComponents.find((node) => node.key === 'slab-earning-model');
      const vals = Array.isArray(existing && existing.values) ? existing.values.slice() : [];
      if (vals.indexOf(earningModel) === -1) {
        vals.unshift(earningModel);
      }
      this.patchFilterComponent('slab-earning-model', {
        values: vals,
        initialSelected: [earningModel]
      });
      if (this.earningModelOptions.indexOf(earningModel) === -1) {
        this.earningModelOptions = [earningModel].concat(this.earningModelOptions);
      }
    }
    this.showFilters = true;
  }

  // Prefill city + service filters from CSCC
  private applyCityServiceQueryParams(): Promise<void> {
    const q = this.route.snapshot.queryParamMap;
    const cityId = (q.get('city') || '').trim();
    const serviceTypes = (q.get('serviceTypes') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    // CSCC sends city id; resolve display name for the name-keyed filter UI.
    let resolvedCityName = '';
    if (cityId) {
      const match = Object.keys(this.cityMap).find(
        (name) => String(this.cityMap[name]) === String(cityId)
      );
      resolvedCityName = match || '';
    }

    if (!resolvedCityName && !serviceTypes.length) {
      return Promise.resolve();
    }

    if (resolvedCityName) {
      this.currentFilters['slab-city'] = [resolvedCityName];
      this.patchFilterComponent('slab-city', {
        initialSelected: [resolvedCityName],
        default: [resolvedCityName]
      });
      this.fetchShifts([resolvedCityName]);
    }

    const citiesForServices = resolvedCityName ? [resolvedCityName] : [];
    return this.fetchServices(citiesForServices).then(() => {
      // CSCC sends serviceDetailId(s) via serviceTypes; resolve names from serviceMap.
      const serviceNames: string[] = [];
      if (serviceTypes.length) {
        Object.keys(this.serviceMap).forEach((name) => {
          if (serviceTypes.indexOf(String(this.serviceMap[name])) !== -1
            && serviceNames.indexOf(name) === -1) {
            serviceNames.push(name);
          }
        });
      }
      if (serviceNames.length) {
        this.currentFilters['slab-services'] = serviceNames;
        const existing = this.filterComponents.find((node) => node.key === 'slab-services');
        const vals = Array.isArray(existing && existing.values) ? existing.values.slice() : [];
        serviceNames.forEach((name) => {
          if (vals.indexOf(name) === -1) {
            vals.unshift(name);
          }
        });
        this.patchFilterComponent('slab-services', {
          values: vals,
          initialSelected: serviceNames.slice(),
          default: serviceNames.slice()
        });
      }
      if (resolvedCityName || serviceNames.length) {
        this.refreshFilterComponents();
        this.showFilters = true;
      }
    });
  }

  private rebuildFilterParamsFromCurrentFilters(): void {
    const filters: SlabFilters = {};
    const allFilters = this.currentFilters;

    if (allFilters['slab-status'] && allFilters['slab-status'] !== 'Both') {
      filters.active = allFilters['slab-status'] === 'Active';
    }

    if (allFilters['slab-fallback'] === 'Fallback Only') {
      filters.isFallbackSlab = true;
    }

    if (allFilters['slab-earning-model'] && allFilters['slab-earning-model'].length > 0) {
      filters.earningModel = allFilters['slab-earning-model'];
    }

    if (allFilters['slab-dates'] && allFilters['slab-dates'].length > 0) {
      if (allFilters['slab-dates'][0]) {
        filters.startDate = allFilters['slab-dates'][0];
      }
      if (allFilters['slab-dates'][1]) {
        filters.startDateLte = allFilters['slab-dates'][1];
      }
    }

    if (allFilters['slab-shifts'] && allFilters['slab-shifts'].length > 0) {
      filters.shifts = allFilters['slab-shifts'];
    }

    if (allFilters['slab-services'] && allFilters['slab-services'].length > 0) {
      filters.serviceDetailIds = allFilters['slab-services'].map(
        (service) => this.serviceMap[service]
      );
    }

    if (allFilters['slab-city'] && allFilters['slab-city'].length > 0) {
      filters.cities = allFilters['slab-city'];
    }

    this.filterParams = filters;
  }

  listen($evt) {
    Object.assign(this.currentFilters, $evt);

    if ('slab-city' in $evt) {
      const selectedCities = $evt['slab-city'];
      if (Array.isArray(selectedCities) && selectedCities.length === 0) {
        this.services = [];
        this.shifts = [];
      }
      if (Array.isArray(selectedCities)) {
        this.fetchServices(selectedCities);
        this.fetchShifts(selectedCities);
      }
    }
  }

  searchByShift() {
    this.filterParams = {
      shift_regex: this.shiftSearch,
    };
    this.fetchTableData();
  }

  applyFilters($event) {
    console.log('Got these filters : ', $event);
    this.table.pagination.setPageNoToZero();
    const filters = $event;

    Object.assign(this.currentFilters, filters);

    this.rebuildFilterParamsFromCurrentFilters();
    this.fetchTableData();
  }
}
